/**
 * Channel Logo Resolver & Official Logo Catalog
 * Provides high-definition, verified official logos for Brazilian and international channels.
 */

export const OFFICIAL_CHANNEL_LOGOS: Record<string, string> = {
  // Band & Grupo Bandeirantes
  'band': 'https://i.imgur.com/1r5T6Pw.png',
  'band hd': 'https://i.imgur.com/1r5T6Pw.png',
  'band sp': 'https://i.imgur.com/1r5T6Pw.png',
  'rede bandeirantes': 'https://i.imgur.com/1r5T6Pw.png',
  'bandeirantes': 'https://i.imgur.com/1r5T6Pw.png',
  'bandnews': 'https://i.ibb.co/0jVZBFp/u8ya-iw-R-400x400.jpg',
  'band news': 'https://i.ibb.co/0jVZBFp/u8ya-iw-R-400x400.jpg',
  'bandsports': 'https://i.imgur.com/EuPvzJe.png',
  'band sports': 'https://i.imgur.com/EuPvzJe.png',

  // Record News (Official 2023-2026 Vector Logo)
  'record news': 'https://upload.wikimedia.org/wikipedia/commons/4/46/Record_News_logo_2023.svg',
  'recordnews': 'https://upload.wikimedia.org/wikipedia/commons/4/46/Record_News_logo_2023.svg',
  'record news brasil': 'https://upload.wikimedia.org/wikipedia/commons/4/46/Record_News_logo_2023.svg',
  'record news hd': 'https://upload.wikimedia.org/wikipedia/commons/4/46/Record_News_logo_2023.svg',
  'pluto tv record news': 'https://images.pluto.tv/channels/6102e04e9ab1db0007a980a1/colorLogoPNG.png',

  // Rede Record
  'record': 'https://upload.wikimedia.org/wikipedia/pt/thumb/1/10/Logotipo_da_Record.png/960px-Logotipo_da_Record.png',
  'record tv': 'https://upload.wikimedia.org/wikipedia/pt/thumb/1/10/Logotipo_da_Record.png/960px-Logotipo_da_Record.png',
  'record hd': 'https://upload.wikimedia.org/wikipedia/pt/thumb/1/10/Logotipo_da_Record.png/960px-Logotipo_da_Record.png',
  'rede record': 'https://upload.wikimedia.org/wikipedia/pt/thumb/1/10/Logotipo_da_Record.png/960px-Logotipo_da_Record.png',
  'record sp': 'https://upload.wikimedia.org/wikipedia/pt/thumb/1/10/Logotipo_da_Record.png/960px-Logotipo_da_Record.png',

  // SBT
  'sbt': 'https://i.imgur.com/tc1ALuu.png',
  'sbt hd': 'https://i.imgur.com/tc1ALuu.png',
  'sbt nacional': 'https://i.imgur.com/tc1ALuu.png',
  'sbt news': 'https://upload.wikimedia.org/wikipedia/commons/4/42/SBT_News_2025.svg',

  // Globo
  'globo': 'https://i.imgur.com/OoSUg78.png',
  'rede globo': 'https://i.imgur.com/OoSUg78.png',
  'tv globo': 'https://i.imgur.com/OoSUg78.png',
  'globo sp': 'https://i.imgur.com/OoSUg78.png',
  'globo rj': 'https://i.imgur.com/OoSUg78.png',
  'globo news': 'https://i.imgur.com/3yZyaCm.png',
  'globonews': 'https://i.imgur.com/3yZyaCm.png',

  // TV Cultura
  'tv cultura': 'https://i.ibb.co/nm0jXMM/cultura-3x.png',
  'cultura': 'https://i.ibb.co/nm0jXMM/cultura-3x.png',

  // Red Bull TV
  'red bull tv': 'https://images.pluto.tv/channels/5e7cb84a172a0f0007da69e4/colorLogoPNG.png',
};

/**
 * Returns the best official logo for a channel by matching its name or tvg-id.
 * If force is true, it always replaces with the official match when available.
 */
export function resolveCorrectChannelLogo(
  name: string,
  currentLogo?: string,
  tvgId?: string,
  force: boolean = false
): string {
  const cleanName = (name || '').toLowerCase().trim();
  const cleanTvgId = (tvgId || '').toLowerCase().trim();

  // Helper to test if current logo is a placeholder, broken or generic
  const isGenericOrBroken =
    !currentLogo ||
    currentLogo.trim() === '' ||
    currentLogo.includes('unsplash.com') ||
    currentLogo.includes('Logotipo_da_Rede_Bandeirantes.png') ||
    currentLogo.includes('CNN_Brasil_logo.svg') ||
    currentLogo.includes('SBT_logo.svg/512px');

  // Specific priority matches:
  if (
    cleanName.includes('record news') ||
    cleanName.includes('recordnews') ||
    cleanTvgId.includes('recordnews') ||
    cleanTvgId.includes('record_news')
  ) {
    if (force || isGenericOrBroken || currentLogo?.includes('unsplash') || currentLogo?.includes('i.imgur.com/sz9gTTr')) {
      return OFFICIAL_CHANNEL_LOGOS['record news'];
    }
  }

  if (cleanName.includes('bandsports') || cleanName.includes('band sports') || cleanTvgId.includes('bandsports')) {
    if (force || isGenericOrBroken) {
      return OFFICIAL_CHANNEL_LOGOS['bandsports'];
    }
  }

  if (cleanName.includes('bandnews') || cleanName.includes('band news') || cleanTvgId.includes('bandnews')) {
    if (force || isGenericOrBroken) {
      return OFFICIAL_CHANNEL_LOGOS['bandnews'];
    }
  }

  if (
    cleanName === 'band' ||
    cleanName.startsWith('band ') ||
    cleanName.startsWith('band-') ||
    cleanName.includes('bandeirantes') ||
    cleanTvgId.startsWith('band.') ||
    cleanTvgId === 'band'
  ) {
    if (force || isGenericOrBroken) {
      return OFFICIAL_CHANNEL_LOGOS['band'];
    }
  }

  if (cleanName.includes('sbt news') || cleanTvgId.includes('sbtnews')) {
    if (force || isGenericOrBroken) {
      return OFFICIAL_CHANNEL_LOGOS['sbt news'];
    }
  }

  if (cleanName === 'sbt' || cleanName.startsWith('sbt ') || cleanTvgId.startsWith('sbt')) {
    if (force || isGenericOrBroken) {
      return OFFICIAL_CHANNEL_LOGOS['sbt'];
    }
  }

  if (cleanName.includes('globonews') || cleanName.includes('globo news')) {
    if (force || isGenericOrBroken) {
      return OFFICIAL_CHANNEL_LOGOS['globonews'];
    }
  }

  if (cleanName.includes('globo') || cleanTvgId.includes('globo')) {
    if (force || isGenericOrBroken) {
      return OFFICIAL_CHANNEL_LOGOS['globo'];
    }
  }

  if (cleanName.includes('cultura') || cleanTvgId.includes('cultura')) {
    if (force || isGenericOrBroken) {
      return OFFICIAL_CHANNEL_LOGOS['tv cultura'];
    }
  }

  if (cleanName.includes('red bull') || cleanTvgId.includes('redbull')) {
    if (force || isGenericOrBroken) {
      return OFFICIAL_CHANNEL_LOGOS['red bull tv'];
    }
  }

  if (cleanName.includes('record') || cleanTvgId.includes('record')) {
    if (force || isGenericOrBroken) {
      return OFFICIAL_CHANNEL_LOGOS['record tv'];
    }
  }

  if (currentLogo && currentLogo.trim() !== '' && !isGenericOrBroken) {
    return currentLogo;
  }

  // Check any other key in catalog
  for (const [key, logoUrl] of Object.entries(OFFICIAL_CHANNEL_LOGOS)) {
    if (cleanName === key || cleanName.includes(key)) {
      return logoUrl;
    }
  }

  if (currentLogo && currentLogo.trim() !== '' && !currentLogo.includes('unsplash')) {
    return currentLogo;
  }

  // Crisp dynamic vector channel badge with initials/name
  const safeName = (name || 'TV').replace(/\[.*?\]|\(.*?\)|FHD|HD|SD|4K/gi, '').trim().slice(0, 10);
  return `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="160" height="90" viewBox="0 0 160 90"><rect width="160" height="90" fill="#18181b" rx="12"/><rect x="2" y="2" width="156" height="86" fill="none" stroke="#27272a" stroke-width="2" rx="10"/><text x="50%" y="54%" fill="#fbbf24" dominant-baseline="middle" text-anchor="middle" font-size="16" font-weight="900" font-family="sans-serif">${safeName}</text></svg>`
  )}`;
}
